import {featureIdentifier} from "../util/featureIdentifier";

/**
 * Build a human-readable label for an atom.
 *
 * Examples:
 *   nameAtom("C")                                  → "Carbon"
 *   nameAtom("C", 0, 0, 1, 5)                      → "Carbon-1"
 *   nameAtom("O", -1)                              → "Oxygen -"
 *   nameAtom("N", 1, 0, 2, 3)                      → "Nitrogen-2 +"
 *
 * The element name comes from `featureIdentifier.getOrganicIdentifierName`,
 * which returns the full element name for most symbols but keeps organic-chemistry
 * overrides ("Pr", "R", "Ac", "Cn", "Ts") as raw symbols.
 *
 * Note: Lone pair information is not included in the name (it's in the description).
 *
 * @param symbol         Element symbol (e.g. "C", "O")
 * @param charge         Formal charge — falsy values produce no charge text
 * @param lonePairCount  Deprecated - kept for API compatibility but ignored
 * @param elementIndex   1-based position among atoms of the same element in the
 *                       containing scope. Only included in the label when
 *                       totalOfElement > 1.
 * @param totalOfElement Total atoms of this element in the containing scope.
 */
export function nameAtom(
    symbol: string,
    charge?: number,
    _lonePairCount?: number,
    elementIndex?: number,
    totalOfElement?: number,
): string {
    let label = featureIdentifier.getOrganicIdentifierName(symbol);

    // Add element index suffix only when there are multiple of this element in scope
    if (
        elementIndex !== undefined &&
        totalOfElement !== undefined &&
        totalOfElement > 1
    ) {
        label += `-${elementIndex}`;
    }

    if (charge) {
        const sign = charge > 0 ? "+" : "-";
        const absCharge = Math.abs(charge);
        label += absCharge === 1 ? ` ${sign}` : ` ${absCharge}${sign}`;
    }

    return label;
}
